import { renderIconToElement } from "@platform/design-system";

import type { MenuItemDef } from "ag-grid-community";

/**
 * Minimal shape required to decide a cell's review state.
 * Satisfied by both BTX FieldCommentResponse and MPX CellReview.
 */
export interface ReviewLike {
  comment?: string | null;
  /** Present on BTX records; absent on MPX CellReview (where existence = flag). */
  needsReview?: boolean;
}

/** The cell a review belongs to, however the surface derives it. */
export interface ReviewMenuTarget {
  planId: string;
  fieldPath: string;
}

/**
 * Generic over the surface's own review record so a caller keeps the fields the
 * shared menu has no use for — the timestamps the comment dialog shows, say.
 */
export interface ReviewMenuCallbacks<TReview extends ReviewLike = ReviewLike> {
  /** Opens the comment dialog, creating the review or editing the existing one. */
  onOpenReview(target: ReviewMenuTarget, existingReview?: TReview): void;
  onClearReview(target: ReviewMenuTarget, existingReview: TReview): void;
}

/**
 * Whether a record counts as an open review.
 *
 * A BTX field comment record carries flags the review doesn't own, so it can
 * exist with needsReview=false (a manual edit, a validated cell). For MPX
 * CellReview, existence of the record IS the flag and needsReview is absent.
 * needsReview !== false reads undefined (MPX) as a review and false as none.
 * A comment alone is not a review: a validated cell keeps its comment.
 */
export const isOpenReview = (review: ReviewLike | undefined): boolean =>
  Boolean(review) && review!.needsReview !== false;

/**
 * Builds the review items for a right-clicked cell, given its already-resolved
 * identity. Each surface derives that identity its own way — from the AG Grid
 * column and row, from a DOM attribute, from a carrier and coverage — but the
 * items, their icons and what counts as a review are the same everywhere.
 *
 * Icons are mounted into fresh DOM elements per menu open so React/MUI's
 * CSS-in-JS sizing applies (ag-grid's `MenuItemDef.icon` is typed
 * `Element | string` and a DOM node can't be reused across mounts).
 */
export const createReviewMenuItems = <TReview extends ReviewLike>(
  target: ReviewMenuTarget,
  existingReview: TReview | undefined,
  { onOpenReview, onClearReview }: ReviewMenuCallbacks<TReview>,
): MenuItemDef[] => {
  if (!isOpenReview(existingReview)) {
    return [
      {
        name: "Needs Review",
        icon: renderIconToElement("view", "sm", "muted"),
        action: () => onOpenReview(target, existingReview),
      },
    ];
  }

  return [
    {
      name: "Edit/View Review",
      icon: renderIconToElement("view", "sm", "muted"),
      action: () => onOpenReview(target, existingReview),
    },
    {
      name: "Clear Review",
      icon: renderIconToElement("remove", "sm", "muted"),
      action: () => onClearReview(target, existingReview!),
    },
  ];
};
